import { AbstractControl, ValidationErrors } from '@angular/forms';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { AttributeEntityType } from 'src/app/shared/models/attributes/attributeEntityType';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import { ParsedAggregateFormula } from 'src/app/shared/models/attributes/aggregateFormula';
import { ParsedArithmeticFormula } from 'src/app/shared/models/attributes/arithmeticFormula';
import { ParsedSwitchFormula } from 'src/app/shared/models/attributes/switchSourceField';

/**
 * Discriminated union of every formula type the summary panel + edit dialog know how to display.
 * Add new kinds here when their parser ships; the surfaces dispatch on `kind` so adding a new
 * shape is mechanical.
 */
export type ParsedFormula =
  | ParsedSwitchFormula
  | ParsedDurationFormula
  | ParsedAggregateFormula
  | ParsedArithmeticFormula;

/**
 * The display format the renderer uses when showing a duration-typed calc field value.
 * Backend always stores raw days; this hint travels with the formula so the renderer can
 * format consistently across surfaces (table cells, profile cards, exports).
 */
export type DurationDisplayFormat = 'days' | 'yearsMonthsDays' | 'years';

/**
 * Operations the duration formula can perform on a source date relative to today. daysSince and
 * daysUntil are arithmetic mirrors (one negates the other); each value pairs with a corresponding
 * evaluator branch in <c>FormulaEvaluationService.EvaluateDuration</c> and a shape-validator case
 * in <c>FormulaShapeValidator.WalkDuration</c>. Keep the three sets in lockstep.
 */
export type DurationOperation = 'daysSince' | 'daysUntil';

/**
 * What a DURATION formula computes its day-count against — either a custom Date attribute
 * (`fieldRef`) or a built-in date system field (`systemFieldRef`, e.g. a Team's StartDate/EndDate).
 * Mirrors the SwitchSourceField custom/system split so the editor picker can group both.
 */
export type DurationSourceField =
  | {
      kind: 'custom';
      attributeDefinitionId: string;
      label: string;
    }
  | {
      kind: 'system';
      fieldName: string;
      label: string;
    };

export interface DurationSystemFieldOption {
  readonly fieldName: string;
  readonly label: string;
}

const TEAM_DURATION_SYSTEM_FIELDS: ReadonlyArray<DurationSystemFieldOption> = [
  // Labels are display-only; the wire `fieldName` (StartDate/EndDate) is what the evaluator
  // resolves. Prefixed with "Team" so they're unambiguous against any custom date attribute.
  { fieldName: 'StartDate', label: 'Team Start Date' },
  { fieldName: 'EndDate', label: 'Team End Date' }
];

/**
 * Date-typed system fields a Duration formula can target, per host entity. Person exposes no
 * system date fields today (its system fields are name/email/address), so it stays custom-only.
 * Team exposes StartDate / EndDate — kept in sync with
 * `TeamValueResolver.BuildSystemFieldValues`, which emits them as `yyyy-MM-dd` so the evaluator's
 * `DateOnly.TryParse` reads them.
 */
export function durationSystemFieldsForEntityType(
  entityType: AttributeEntityType | null | undefined
): ReadonlyArray<DurationSystemFieldOption> {
  return entityType === AttributeEntityType.Team ? TEAM_DURATION_SYSTEM_FIELDS : [];
}

/**
 * Shape of the DurationEditorComponent's `form.value` payload. The parent reads this on submit
 * and feeds it through {@link buildDurationFormulaJson}.
 */
export interface DurationEditorState {
  sourceField: DurationSourceField | null;
  operation: DurationOperation;
  displayFormat: DurationDisplayFormat;
}

/**
 * Validates the duration editor's `sourceField` control holds a fully-resolved
 * {@link DurationSourceField}. The autocomplete pattern writes the raw typed string into the
 * control on every keystroke and only promotes to an object on `optionSelected`; without this
 * check `Validators.required` would let an admin save mid-typing.
 */
export function durationSourceFieldRequiredValidator(
  control: AbstractControl
): ValidationErrors | null {
  const v = control.value;
  if (v && typeof v === 'object' && (v.kind === 'custom' || v.kind === 'system')) {
    return null;
  }
  return { required: true };
}

/**
 * Serializes the DurationEditor form state into the formula JSON expected by the backend
 * evaluator (see <c>FormulaEvaluationService.EvaluateDuration</c>). The output is the
 * canonical shape — `displayFormat` is always emitted explicitly so callers don't have to
 * track defaults across the wire.
 */
export function buildDurationFormulaJson(state: DurationEditorState): string {
  if (!state.sourceField) {
    throw new Error('buildDurationFormulaJson requires a sourceField');
  }
  const field =
    state.sourceField.kind === 'system'
      ? { type: 'systemFieldRef', fieldName: state.sourceField.fieldName }
      : { type: 'fieldRef', attributeDefinitionId: state.sourceField.attributeDefinitionId };
  const payload = {
    type: 'duration',
    operation: state.operation,
    field,
    displayFormat: state.displayFormat
  };
  return JSON.stringify(payload);
}

/**
 * Result shape from {@link parseDurationFormulaJson}. Mirrors {@link ParsedSwitchFormula} so the
 * summary panel and edit dialog can hold either via a discriminated union and dispatch by `kind`.
 */
export interface ParsedDurationFormula {
  readonly kind: 'duration';
  state: DurationEditorState;
  /** Same lifecycle states the Switch parser uses for the source field. */
  sourceFieldStatus: 'resolved' | 'missing';
}

/**
 * Inverse of {@link buildDurationFormulaJson}. Resolves the source field's display label from
 * the supplied custom-fields lookup so the summary panel can render it without re-fetching.
 *
 * Throws on malformed JSON or a non-duration root type — callers should narrow on `root.type`
 * upstream (the summary component dispatches by root type) so a Switch formula never reaches
 * this parser.
 */
export function parseDurationFormulaJson(
  formulaJson: string,
  customFieldsById: ReadonlyMap<string, AttributeDefinition>,
  entityType: AttributeEntityType = AttributeEntityType.Person
): ParsedDurationFormula {
  const root = JSON.parse(formulaJson);
  if (!root || typeof root !== 'object') {
    throw new Error(`Formula root must be an object, got '${typeof root}'`);
  }
  if (root.type !== 'duration') {
    throw new Error(`parseDurationFormulaJson called on '${root.type}' formula`);
  }

  const fieldNode = root.field;
  if (!fieldNode || typeof fieldNode !== 'object') {
    throw new Error("'duration' formula is missing its 'field' object");
  }

  let sourceField: DurationSourceField | null = null;
  let sourceFieldStatus: ParsedDurationFormula['sourceFieldStatus'] = 'resolved';
  if (fieldNode.type === 'fieldRef') {
    const id = String(fieldNode.attributeDefinitionId ?? '');
    const def = customFieldsById.get(id);
    if (def) {
      sourceField = { kind: 'custom', attributeDefinitionId: def.id, label: def.name };
    } else {
      sourceFieldStatus = 'missing';
    }
  } else if (fieldNode.type === 'systemFieldRef') {
    const fieldName = String(fieldNode.fieldName ?? '');
    const known = durationSystemFieldsForEntityType(entityType).find(
      (f) => f.fieldName === fieldName
    );
    sourceField = { kind: 'system', fieldName, label: known?.label ?? fieldName };
    // An unrecognized system field name (renamed/removed, or authored for a different entity)
    // surfaces as "missing" so the dialog warns rather than silently rendering an orphan.
    if (!known) {
      sourceFieldStatus = 'missing';
    }
  } else {
    throw new Error(`Unsupported duration field reference type '${fieldNode.type}'`);
  }

  // Operations are semantic — daysSince vs daysUntil flip the sign convention. Unlike
  // displayFormat (which falls back to 'days' for forward-compat readability), an unknown
  // operation must throw so the summary panel renders "This formula can't be displayed: …"
  // and the admin updates their client rather than the parser silently picking the wrong
  // direction.
  if (root.operation !== 'daysSince' && root.operation !== 'daysUntil') {
    throw new Error(`Unsupported duration operation '${root.operation}'`);
  }
  const operation: DurationOperation = root.operation;
  const displayFormat: DurationDisplayFormat =
    root.displayFormat === 'yearsMonthsDays'
      ? 'yearsMonthsDays'
      : root.displayFormat === 'years'
        ? 'years'
        : 'days';

  return {
    kind: 'duration',
    state: { sourceField, operation, displayFormat },
    sourceFieldStatus
  };
}

/**
 * Returns the duration `displayFormat` for an {@link AttributeDefinition}, or `null` when the
 * field isn't a duration calc field. Cell-rendering surfaces should prefer this over coding
 * the `uiType === CalculatedField + readDurationDisplayFormat` lookup inline.
 */
export function getDurationDisplayFormat(
  customField: AttributeDefinition | null | undefined
): DurationDisplayFormat | null {
  if (!customField || customField.uiType !== AttributeUiType.CalculatedField) return null;
  return readDurationDisplayFormat(customField.formulaExpression);
}

/**
 * Returns the duration `displayFormat` carried on a formula JSON string, or `null` when the
 * formula isn't a duration node. Lower-level than {@link getDurationDisplayFormat} — most
 * callers should prefer that one since it takes the AttributeDefinition directly. This is
 * the underlying JSON-aware step used by both readers.
 *
 * Returns `null` (rather than throwing) on malformed JSON so a corrupted formula falls back to
 * raw rendering instead of breaking the table cell.
 */
export function readDurationDisplayFormat(
  formulaExpression: string | null | undefined
): DurationDisplayFormat | null {
  if (!formulaExpression) return null;
  try {
    const root = JSON.parse(formulaExpression);
    if (!root || typeof root !== 'object' || root.type !== 'duration') return null;
    if (root.displayFormat === 'yearsMonthsDays') return 'yearsMonthsDays';
    if (root.displayFormat === 'years') return 'years';
    return 'days';
  } catch {
    return null;
  }
}

/**
 * Formats a raw signed-integer day count (the materialized value of a duration calc field) into
 * the user-facing string the calc field's `displayFormat` configures. Pure function so it can be
 * called either through the {@link DaysFormattedPipe} (templates) or directly from component
 * code (table-cell helpers, exports). Empty/null/non-numeric input renders as the empty string.
 */
export function formatDays(
  value: string | number | null | undefined,
  format: DurationDisplayFormat
): string {
  if (value === null || value === undefined || value === '') return '';
  const days = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(days)) return '';

  if (format === 'years') {
    // Decimal years using a 365-day year (consistent with yearsMonthsDays' integer math), to
    // 3 decimal places when there's actually a fractional part. Integer day counts that fall on
    // a whole year render as "1 year" / "2 years" — no "1.000 years" noise. The check is on the
    // integer day count rather than a float comparison so it's exact.
    const isWholeYears = days % 365 === 0;
    if (isWholeYears) {
      const whole = days / 365;
      return `${whole} ${Math.abs(whole) === 1 ? 'year' : 'years'}`;
    }
    return `${(days / 365).toFixed(3)} years`;
  }

  if (format === 'yearsMonthsDays') {
    // Integer decomposition using 365-day years and 30-day months. Earlier the formatter ran
    // moment.duration which uses 365.25/30.44 internally — accurate for a duration concept but
    // surprising for users (365 days rendered as "11 months, 30 days"). For age/tenure UX,
    // round-number inputs should yield round-number outputs: 365 → "1 year", 30 → "1 month".
    // Drop zero components so "0 years, 0 months, 6 days" reads as just "6 days" and
    // "3 years, 0 months, 6 days" reads as "3 years, 6 days". If everything is zero (source
    // date == today), fall back to "0 days" so the cell never renders empty.
    const sign = days < 0 ? '-' : '';
    const absDays = Math.abs(days);
    const years = Math.floor(absDays / 365);
    const monthsAndDays = absDays % 365;
    const months = Math.floor(monthsAndDays / 30);
    const remainderDays = monthsAndDays % 30;
    const parts: string[] = [];
    if (years !== 0) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
    if (months !== 0) parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
    if (remainderDays !== 0) parts.push(`${remainderDays} ${remainderDays === 1 ? 'day' : 'days'}`);
    if (parts.length === 0) return '0 days';
    return `${sign}${parts.join(', ')}`;
  }

  // 'days' format: include a units suffix so the value reads as self-describing on surfaces
  // where the calc field's name isn't visible (profile cards in narrow layouts, exports, etc.).
  // Pluralize for ±1 — `1 day` / `-1 day`, `0 days` / `2 days`.
  return `${days} ${Math.abs(days) === 1 ? 'day' : 'days'}`;
}
